import type { IscEnrollmentStatusDto } from '@act/shared/data-transfer-objects';
import {
  IscEnrollmentStatusUpdatedWSPayload,
  IscEnrollmentWSEvents,
} from '@act/shared/socket-events';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/storeHooks';
import type { WebsocketEventActions } from '../types/websocketTypes';

/** Redux state for ISC enrollment gate DTOs keyed by member id. */
export interface IscEnrollmentState {
  byMemberId: Record<string, IscEnrollmentStatusDto>;
  /**
   * Monotonic epoch per member, bumped on each STATUS_UPDATED websocket write.
   * HTTP upserts pass the epoch observed when the GET started; if a WS write
   * landed afterward, the stale HTTP response is ignored.
   */
  realtimeEpochByMemberId: Record<string, number>;
}

const initialState: IscEnrollmentState = {
  byMemberId: {},
  realtimeEpochByMemberId: {},
};

/** Payload for an HTTP enrollment status upsert with stale-guard epoch. */
export interface UpsertIscEnrollmentStatusPayload {
  status: IscEnrollmentStatusDto;
  /** `realtimeEpochByMemberId` for this member when the GET started. */
  requestRealtimeEpoch: number;
}

/**
 * Caches ISC enrollment gate DTOs from GET and realtime projection syncs.
 */
export const iscEnrollmentSlice = createSlice({
  name: 'iscEnrollment',
  initialState,
  reducers: {
    /**
     * Upserts one member's enrollment gate DTO from HTTP, unless a websocket
     * write for that member landed after the request started.
     *
     * @param state - Slice state
     * @param action - Status DTO plus the realtime epoch at GET start
     */
    upsertStatus: (
      state,
      action: PayloadAction<UpsertIscEnrollmentStatusPayload>
    ) => {
      const { status, requestRealtimeEpoch } = action.payload;
      const currentEpoch =
        state.realtimeEpochByMemberId[status.memberId] ?? 0;
      if (currentEpoch > requestRealtimeEpoch) {
        return;
      }
      state.byMemberId[status.memberId] = status;
    },
    /**
     * Applies a websocket enrollment status payload and bumps the member's
     * realtime epoch so in-flight HTTP upserts cannot overwrite it.
     *
     * @param state - Slice state
     * @param action - WS payload with enrollmentStatus
     */
    statusUpdatedFromWebsocket: (
      state,
      action: PayloadAction<IscEnrollmentStatusUpdatedWSPayload>
    ) => {
      const { enrollmentStatus } = action.payload;
      const memberId = enrollmentStatus.memberId;
      state.byMemberId[memberId] = enrollmentStatus;
      state.realtimeEpochByMemberId[memberId] =
        (state.realtimeEpochByMemberId[memberId] ?? 0) + 1;
    },
    /**
     * Clears cached status when leaving a member (optional hygiene).
     *
     * @param state - Slice state
     * @param action - Member id to clear
     */
    clearMemberStatus: (state, action: PayloadAction<string>) => {
      delete state.byMemberId[action.payload];
      delete state.realtimeEpochByMemberId[action.payload];
    },
  },
});

/** ISC enrollment reducer for the Care Portal store. */
export const iscEnrollmentReducer = iscEnrollmentSlice.reducer;

/** ISC enrollment slice actions. */
export const iscEnrollmentActions = iscEnrollmentSlice.actions;

/**
 * Selects cached enrollment status for a member.
 *
 * @param state - Root state with iscEnrollment slice
 * @param memberId - Member user id
 * @returns DTO or undefined
 */
export const selectIscEnrollmentStatusByMemberId = (
  state: { iscEnrollment?: IscEnrollmentState },
  memberId: string
): IscEnrollmentStatusDto | undefined =>
  state.iscEnrollment?.byMemberId?.[memberId];

/**
 * Selects the realtime (websocket) epoch for a member's enrollment cache.
 *
 * @param state - Root state with iscEnrollment slice
 * @param memberId - Member user id
 * @returns Epoch (0 when never updated via websocket)
 */
export const selectIscEnrollmentRealtimeEpochByMemberId = (
  state: { iscEnrollment?: IscEnrollmentState },
  memberId: string
): number => state.iscEnrollment?.realtimeEpochByMemberId?.[memberId] ?? 0;

/** Hook selectors for ISC enrollment cache. */
export const iscEnrollmentSelectors = {
  /**
   * @param memberId - Member user id
   * @returns Cached enrollment DTO or undefined
   */
  useSelectStatusByMemberId: (memberId: string) =>
    useAppSelector(state =>
      selectIscEnrollmentStatusByMemberId(state, memberId)
    ),
  /**
   * @param memberId - Member user id
   * @returns Realtime epoch for stale HTTP upsert guards
   */
  useSelectRealtimeEpochByMemberId: (memberId: string) =>
    useAppSelector(state =>
      selectIscEnrollmentRealtimeEpochByMemberId(state, memberId)
    ),
};

/** Websocket → Redux wiring for ISC enrollment projection updates. */
export const iscEnrollmentWebsocketActions: WebsocketEventActions[] = [
  {
    type: IscEnrollmentWSEvents.STATUS_UPDATED,
    action: iscEnrollmentSlice.actions.statusUpdatedFromWebsocket,
    channel: 'baseSocket',
  },
];
